"use client";

import { Fragment } from "react";
import Link from "next/link";
import { track } from "@/lib/analytics";
import type { CatalogShelf, ProgramListItem } from "@/lib/api/api-client";
import { LIBRARY_SELECTION_PATH, librarySelectionPath } from "@/lib/library/library-routes";
import { FadeUpSection } from "@/components/ui/organisms/piece-overview/fade-up-section";
import { LearnCallout } from "./learn-callout";
import { CatalogCard } from "./catalog-card";

/**
 * L'accueil de la bibliothèque : des sélections, pas un formulaire.
 *
 * # Pas d'interstitiel, pas de questionnaire d'entrée
 *
 * On aurait pu demander « quel est ton niveau ? » avant de montrer quoi que ce
 * soit. Deux raisons de ne pas le faire : la personne ne sait pas répondre —
 * c'est justement ce qu'elle vient découvrir — et un écran qui ne montre aucun
 * morceau est un écran qu'on quitte. Les sélections répondent à la question sans
 * la poser.
 *
 * # Ce que le serveur décide, ce que l'écran décide
 *
 * L'ordre des sélections, leur contenu et leur intitulé viennent de `/shelves` :
 * la curation est éditoriale et vit du côté qui a les données. L'écran ne
 * décide que de la mise en page, et **ne réordonne jamais** ce qu'il reçoit.
 *
 * # Les cartes en grille, puis en carrousel
 *
 * Sous 1024 px, une sélection passe en défilement horizontal avec accrochage
 * (`scroll-snap`). La raison est la longueur de page, pas la troncature :
 * `auto-fill minmax(200px, 1fr)` ne déborde jamais, il passe à la ligne, et à
 * 360 px il donne une colonne — soit huit cartes empilées par sélection, près
 * de douze mille pixels de page.
 *
 * On a longtemps écrit ici que le carrousel « dit qu'il y a une suite ». C'est
 * faux, et un utilisateur l'a payé : la carte coupée qui portait ce message
 * n'existe qu'à certaines largeurs, et il y a trois bandes de 69 px où le rail
 * s'arrête pile sur une carte pleine. Le signal est désormais explicite — un
 * voile de bord, et le compte dans l'en-tête.
 */

type Props = {
  shelves: readonly CatalogShelf[];
  drafts: boolean;
  /** `null` quand l'API n'a pas répondu : mieux vaut pas d'interstitiel qu'une
   *  carte vide. Le header porte de toute façon l'onglet « Apprendre ». */
  introProgram: ProgramListItem | null;
};

export function LibraryHome({ shelves, drafts, introProgram }: Props) {
  return (
    <div className="flex flex-col gap-10 pb-14">
      {shelves.map((shelf, index) => (
        /*
          L'entrée en cascade porte sur la sélection entière et non sur chaque
          carte. Par carte, le décalage de `translateY` n'était pas résorbé au
          même instant sur toute une rangée : le bas des cartes en était
          désaligné le temps de l'animation, ce qui se lit comme une grille
          cassée. Une sélection à la fois, c'est aussi plus calme à l'œil.
        */
        <Fragment key={shelf.key}>
          <FadeUpSection delay={Math.min(index, 4) * 60}>
            <ShelfRow shelf={shelf} drafts={drafts} />
          </FadeUpSection>

          {/*
            Le renvoi vers le parcours s'intercale après la première sélection,
            et non en pied de page.

            En pied, il n'était lu que par qui avait déjà fait défiler tout le
            catalogue, c'est-à-dire par quelqu'un qui n'avait justement pas
            trouvé son morceau. Ici il arrive après avoir montré qu'il y a des
            morceaux — la première sélection est passée — mais avant que les
            quatre suivantes n'aient eu le temps de convaincre qu'on saura les
            jouer. C'est le moment exact où la question se pose.
          */}
          {index === 0 && introProgram !== null && (
            <FadeUpSection delay={60}>
              <LearnCallout program={introProgram} />
            </FadeUpSection>
          )}
        </Fragment>
      ))}

      {/*
        Filet : sans sélection à afficher, l'interstitiel ci-dessus n'est jamais
        rendu et la page perdrait sa seule sortie vers le parcours.
      */}
      {shelves.length === 0 && introProgram !== null && <LearnCallout program={introProgram} />}

      <LibraryIntro />
    </div>
  );
}

/**
 * Ce que la bibliothèque prétend faire, en toutes lettres.
 *
 * En pied et non sous le titre : l'écran doit montrer des morceaux tout de
 * suite — « un écran qui ne montre aucun morceau est un écran qu'on quitte » —
 * et cinq phrases entre la question et la première carte repousseraient la
 * grille sous la ligne de flottaison. En bas, le texte est lu par qui a fait
 * défiler sans rien trouver, et par les moteurs, qui ne défilent pas.
 */
function LibraryIntro() {
  return (
    <section className="mt-4 border-t border-border-subtle pt-8">
      <h2 className="text-[18px] font-medium leading-snug tracking-[-0.01em] text-text-heading">
        Comment les morceaux sont classés
      </h2>

      <p className="mt-2 max-w-[70ch] text-[14px] leading-relaxed text-text-soft">
        Il n&apos;y a pas de morceau facile en soi. Il y a des morceaux dont les mains font peu de choses à la fois.
        C&apos;est ça qu&apos;on affiche. Chaque fiche te dit ce que ta main doit ouvrir, si elle doit se déplacer,
        combien de durées de note tu auras à tenir, s&apos;il y a des touches noires, et dans quelle unité de temps le
        morceau se travaille&nbsp;: une soirée, une semaine, un mois. Tout s&apos;écoute avant d&apos;être choisi, et
        tout se joue sans partition ni solfège&nbsp;: les notes descendent sur le clavier, et on les nomme après les
        avoir jouées, pas avant. Si tu ne sais pas par où commencer,{" "}
        <Link
          href={LIBRARY_SELECTION_PATH}
          className="text-accent-bright transition-colors duration-150 hover:text-text-primary"
        >
          les sélections
        </Link>{" "}
        ont déjà fait le tri.
      </p>
    </section>
  );
}

function ShelfRow({ shelf, drafts }: { shelf: CatalogShelf; drafts: boolean }) {
  // Une sélection curée à la main n'a pas de requête à rejouer : sans « voir
  // les N », la sélection *est* la liste complète, et promettre une suite qui
  // n'existe pas serait pire que de ne rien promettre.
  const canOpen = shelf.query !== null && shelf.total > shelf.items.length;

  /*
    Le rail déborde toujours à partir de quatre cartes.

    Sous 1024 px, le plus large scrollport possible fait 727 px (à 1023 px de
    fenêtre) et quatre cartes en occupent 848. La condition est donc décidable
    au rendu, sans mesurer quoi que ce soit dans le navigateur — ce qui compte
    ici, puisque la page doit sortir complète du serveur.
  */
  const scrolls = shelf.items.length >= 4;

  return (
    <section className="flex flex-col gap-3">
      {/*
        `items-baseline` et non `items-end` : la phrase d'intention fait une
        ligne à cinq colonnes et deux à trois colonnes, donc le compte à droite
        sautait verticalement d'une sélection à l'autre et d'un palier à
        l'autre.
      */}
      <header className="flex items-baseline justify-between gap-4 px-1">
        <div className="min-w-0">
          {/*
            18 px, casse normale, couleur pleine.

            C'était 11 px en capitales espacées et à 60 % d'opacité : la
            grammaire d'une étiquette, pas d'un titre. L'œil la lisait comme
            une métadonnée et cherchait le vrai titre en dessous, où il ne
            trouvait que la phrase d'intention, plus faible encore. Résultat
            mesuré : le titre de section était le plus petit texte de l'écran,
            plus petit que la légende sous chaque carte. Un premier retour
            utilisateur l'a signalé tel quel.

            L'échelle est maintenant 32 (la question) / 18 (les sections) /
            15 (les morceaux).
          */}
          <h2 className="text-[18px] font-medium leading-snug tracking-[-0.01em] text-text-heading">{shelf.title}</h2>
          <p className="mt-1.5 max-w-[68ch] text-[13px] leading-snug text-text-soft">{shelf.intent}</p>
        </div>

        {/*
          Le compte est affiché dans tous les cas, lien ou non.

          Sur « Tes cinq premiers morceaux », curée et complète, il n'y avait
          rien : aucun élément d'interface ne disait combien la sélection
          contenait, sur la première section de la page, celle qui essuie le
          premier regard. Quelqu'un qui en voyait trois en déduisait qu'il y en
          avait trois.
        */}
        {canOpen ? (
          <Link
            href={librarySelectionPath(shelf.key, drafts)}
            onClick={() =>
              track({
                name: "catalog_shelf_opened",
                properties: {
                  shelf: shelf.key,
                  target: "see_all",
                  position: -1,
                },
              })
            }
            className="shrink-0 rounded-sm text-[13px] text-accent-bright transition-colors duration-150 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            voir les {shelf.total} →
          </Link>
        ) : (
          <span className="shrink-0 text-[13px] text-text-soft">{shelf.items.length} morceaux</span>
        )}
      </header>

      {/*
        Une seule liste de cartes pour les deux mises en page : la grille et le
        carrousel ne diffèrent que par des classes, donc React ne démonte rien
        au franchissement du seuil et l'image ne clignote pas.

        Écrit dans le sens mobile-first (carrousel de base, `lg:` rétablit la
        grille) et non l'inverse : deux règles de même spécificité se départagent
        à l'ordre de la feuille, et l'ordre des variantes `max-*` de Tailwind
        n'est pas quelque chose sur quoi faire reposer ce qui s'affiche.

        Le `relative` porte le voile de bord ci-dessous.
      */}
      <div className="relative">
        <div
          className="
            flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-1 px-1 pb-2
            lg:grid lg:overflow-visible lg:snap-none lg:mx-0 lg:px-0 lg:pb-0
          "
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          }}
        >
          {shelf.items.map((piece, index) => {
            const reason = shelf.reasons[piece.id];

            return (
              /*
              `self-start` : sans lui la cellule est étirée à la hauteur de la
              rangée, et la carte — qui porte `h-full` pour cette raison même —
              absorbe tout ce que la raison ne prend pas. Résultat mesuré :
              cartes de 190, 207 et 224 px selon la longueur du texte en
              dessous, donc des tuiles qui ne s'alignent plus.

              La carte n'a plus besoin d'être étirée ici : le titre est en
              recouvrement, donc il ne compte pas dans la hauteur, et tuile
              (156) + ligne de geste (32) est identique d'une carte à l'autre.
              C'est la raison, elle, qui varie — et une longueur de paragraphe
              n'a aucune raison de déformer l'image au-dessus.
            */
              <div
                key={piece.id}
                className="shrink-0 w-[200px] snap-start self-start lg:w-auto lg:shrink flex flex-col"
              >
                <CatalogCard
                  piece={piece}
                  onOpen={() =>
                    track({
                      name: "catalog_shelf_opened",
                      properties: {
                        shelf: shelf.key,
                        target: "piece",
                        position: index,
                      },
                    })
                  }
                />

                {/*
                La raison d'une sélection manuelle, lisible sans interaction.

                Sans elle, une sélection curée ne se distingue en rien d'un tri : c'est
                la phrase qui dit *pourquoi ce morceau, à cette place* qui fait
                toute la valeur du travail éditorial. La cacher derrière un
                survol reviendrait à ne pas l'avoir écrite — et priverait le
                tactile, où il n'y a pas de survol.

                Hors du cadre de la carte, sous elle : la carte porte l'identité
                du morceau, cette phrase porte l'avis de quelqu'un. Les mélanger
                ferait passer un choix pour une donnée.
              */}
                {reason !== undefined ? (
                  <p className="mt-2 px-0.5 text-[11.5px] leading-[1.45] text-text-soft">{reason}</p>
                ) : null}
              </div>
            );
          })}
        </div>

        {/*
          Le voile qui dit que ça continue à droite.

          On croyait la troncature suffisante : une carte coupée se voit, donc
          elle annonce la suite. C'est vrai par intermittence seulement. La
          largeur de coupe dépend de la fenêtre, et il existe trois bandes de
          69 px où le rail s'arrête pile sur une carte pleine — dont 903 à
          971 px, soit un navigateur en demi-écran sur un 1920. Là, trois
          cartes complètes alignées au bord droit et rien après : c'est
          exactement ce qu'un premier utilisateur a décrit, il n'avait pas
          compris qu'on pouvait faire défiler.

          Décoratif et inerte (`aria-hidden`, `pointer-events-none`) : le compte
          dans l'en-tête porte déjà l'information pour qui n'y voit rien, et un
          calque qui capterait le pointeur mangerait le défilement tactile.
        */}
        {scrolls && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-end pb-2 pr-0.5 lg:hidden"
            style={{
              background: "linear-gradient(to left, var(--color-bg-deep) 30%, transparent)",
            }}
          >
            <ChevronRightIcon />
          </div>
        )}
      </div>
    </section>
  );
}

/** Le chevron du voile de bord. Purement décoratif, voir son appelant. */
function ChevronRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-text-secondary"
    >
      <path d="M6 3.5 10.5 8 6 12.5" />
    </svg>
  );
}
